/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
import { getCustomerFlow, trackIt } from '@vfde-fe/sails_core';
import {
    TRACKING_APPLIED_FILTERS,
    TRACKING_TYPE_INFORMATION,
    TRACKING_ERROR_ACTION,
    TRACKING_ERROR_AREA,
    TRACKING_ERROR_NOTIFICATION,
    ACCORDION,
    TRACKING_FLOW_TYPE,
    TRACKING_PRODUCT_LINE,
    SALECSCHANNEL_MAP,
} from '../../../constant';
import {
    TRACKING_BUNDLE,
    TRACKING_DEVICE,
    TRACKING_ERROR_TYPE_TECHNICAL,
    TRACKING_INFORMATION_ACTION_REVEAL,
    TRACKING_INFORMATION_TRIGGER_ACCORDION,
    TRACKING_INFORMATION_UI_TYPE_FAQ,
    TRACKING_NEW_CONTRACT,
    TRACKING_NOT_LOGGED_IN,
    TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
    TRACKING_PLATFORM_TYPE_RESPONSIVE,
    TRACKING_PRODUCT_TYPE,
    TRACKING_SITE_AREA_MOBILITY,
    TRACKING_TARIFF, TRACK_PAGE_VIEW,
} from '@vfde-fe/sails-constants';
import PageViewTrackingPayload from '../../../interface/tracking/PageViewTrackingPayload';
import { ApiError } from 'app/interface/dto/ApiError';
import { getHotlineNumbers } from '../../../helper/additionalPageOptionsHelper';
import aProduct from 'app/interface/tracking/aProducts';
import { SelectedOption } from '../interface';
import { SalesChannel } from '../../../enum';
import { getEligiblePrice } from '../../../helper/priceHelper';
import { Accordion } from '@vfde-brix/accordion';
import { formatToTrackingString } from '../../../helper/formatter';
import { getGigacubeObjectProperty } from '../../../helper/windowObjectHelper';
import { Notification } from '@vfde-brix/notification';

/**
 * Create the page view tracking payload
 */
export const trackPageView = (selectedOption: SelectedOption, salesChannel: SalesChannel) => {
    const { subscription, hardware } = selectedOption;

    if (!subscription || !hardware) {
        return;
    }

    const lineOfCustomer = getCustomerFlow(SALECSCHANNEL_MAP[salesChannel]);
    const aProducts: aProduct[] = [
        {
            name: hardware.label,
            sku: hardware.backendId,
            type: TRACKING_DEVICE,
            price_once: subscription?.prices?.composition?.hardware?.onetime?.withDiscounts?.gross !== undefined ?
                subscription?.prices?.composition?.hardware?.onetime?.withDiscounts?.gross.toString() :
                subscription?.prices?.composition?.hardware?.onetime?.withoutDiscounts?.gross !== undefined ?
                    subscription?.prices?.composition?.hardware?.onetime?.withoutDiscounts?.gross.toString() :
                    null,
            proposition: 1,
            units: 1,
            atomic_id: hardware.hardwareId.toString(),
        },
        {
            sku: subscription.legacyGroupId,
            name: subscription?.tariffName,
            type: TRACKING_TARIFF,
            price_once: selectedOption.subscription?.prices?.composition?.tariff?.onetime?.withDiscounts?.gross !== undefined ?
                selectedOption.subscription?.prices?.composition?.tariff?.onetime?.withDiscounts?.gross.toString() :
                selectedOption.subscription?.prices?.composition?.tariff?.onetime?.withoutDiscounts?.gross !== undefined ?
                    selectedOption.subscription?.prices?.composition?.tariff?.onetime?.withoutDiscounts?.gross.toString() :
                    null,
            price_monthly: getEligiblePrice(subscription).toString(),
            duration: 24,
            proposition: 1,
            units: 1,
        },
    ];

    const pageView: PageViewTrackingPayload = {
        page_levels: window.VF?.siteStructure,
        page_type: TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
        site_area: TRACKING_SITE_AREA_MOBILITY,
        login_status: TRACKING_NOT_LOGGED_IN,
        platform_type: TRACKING_PLATFORM_TYPE_RESPONSIVE,
        flow_type: TRACKING_FLOW_TYPE,
        business_transaction_type: TRACKING_NEW_CONTRACT,
        line_of_customer: lineOfCustomer,
        oOrder: {
            aProducts,
            aPropositions: [{
                id: 1,
                name: `${hardware.label}|${subscription.tariffName}`,
                target_audience: lineOfCustomer,
                retention: TRACKING_NEW_CONTRACT,
                product_type: TRACKING_PRODUCT_TYPE,
                product_line: TRACKING_PRODUCT_LINE,
                product_category: TRACKING_BUNDLE,
                units: 1,
            }],
        },
        applied_filters: TRACKING_APPLIED_FILTERS,
        hotline_numbers: getHotlineNumbers(),
    };

    trackIt(TRACK_PAGE_VIEW, pageView);
};

/**
 * Track overlay
 */
export const trackOverlay = (linkText: string) => {
    const trackingData = {
        information_action: TRACKING_INFORMATION_ACTION_REVEAL,
        information_name: formatToTrackingString(linkText),
        information_ui_type: 'overlay',
        information_trigger: 'link',
    };

    trackIt(TRACKING_TYPE_INFORMATION, trackingData);
};

/**
 * Tracks FAQ accordions
 */
export const trackFaqAccordionShown = (accordionId?: string) => {
    const accordionComp: Accordion | undefined = getGigacubeObjectProperty(ACCORDION);
    const accordionItems = accordionComp?.getProperties().items;
    const item = accordionItems?.find(i => i.stdId === accordionId);
    const headline = item?.stdHeadline || `accordion-${accordionId}`;

    trackIt(TRACKING_TYPE_INFORMATION, {
        information_action: TRACKING_INFORMATION_ACTION_REVEAL,
        information_name: formatToTrackingString(headline),
        information_ui_type: TRACKING_INFORMATION_UI_TYPE_FAQ,
        information_trigger: TRACKING_INFORMATION_TRIGGER_ACCORDION,
    });
};

/**
 * Track footnote
 * @param text The text of the element that was interacted with by the customer
 */
export const trackFootnote = (text: string) => {
    const trackingData = {
        information_action: TRACKING_INFORMATION_ACTION_REVEAL,
        information_name: formatToTrackingString(text),
        information_ui_type: 'footnote',
        information_trigger: 'reference footnote',
    };

    trackIt(TRACKING_TYPE_INFORMATION, trackingData);
};

/**
 * Track error notification
 */
export const trackErrorNotification = (error: ApiError, notification: Notification | null) => {
    const message = notification?.getContainerElement().textContent;
    const trackingErrorPayload = {
        error_action: TRACKING_ERROR_ACTION,
        error_type: TRACKING_ERROR_TYPE_TECHNICAL,
        error_message: formatToTrackingString(message || ''),
        error_area: TRACKING_ERROR_AREA,
        error_code: error?.httpCode?.toString(),
    };

    trackIt(TRACKING_ERROR_NOTIFICATION, trackingErrorPayload);
};
